// ✅ src/router/index.js
import { createRouter, createWebHistory } from 'vue-router';
import store from '@/store';

const routes = [
  {
    path: '/',
    name: 'booking-engine',
    component: () => import('@/views/BookingServiceEntry.vue'),
  },
  {
    path: '/activities/',
    name: 'tours-booking-engine',
    component: () => import('@/views/ToursBookingMain.vue'),
  },
  {
    path: '/restaurant/',
    name: 'restaurant-booking-engine',
    component: () => import('@/views/BookingServiceEntry.vue'),
  },
  {
    path: '/confirmation/:bookingId',
    name: 'public-reservation-confirmation',
    component: () => import('@/views/PublicReservationConfirmation.vue'),
    props: true,
  },
  {
    path: '/tour-confirmation/:bookingId',
    name: 'public-tour-reservation-confirmation',
    component: () => import('@/views/PublicTourReservationConfirmation.vue'),
    props: true,
  },
  {
    path: '/restaurant-confirmation/:reservationCode',
    name: 'public-restaurant-reservation-confirmation',
    component: () => import('@/views/PublicRestaurantReservationConfirmation.vue'),
    props: true,
  },
  {
    path: '/restaurant-benefits/:token',
    name: 'public-restaurant-benefits',
    component: () => import('@/views/PublicRestaurantBenefits.vue'),
    props: true,
    meta: { hideLiveChat: true },
  },
  {
    path: '/menu/:token',
    name: 'restaurant-menu-guest',
    component: () => import('@/views/restaurant/RestaurantTableGuest.vue'),
    meta: { hideLiveChat: true, publicLinkScope: 'menu' },
  },
  {
    path: '/table/:token',
    name: 'restaurant-table-guest',
    component: () => import('@/views/restaurant/RestaurantTableGuest.vue'),
    meta: { hideLiveChat: true, publicLinkScope: 'table' },
  },
  {
    path: '/mesa/:token',
    name: 'restaurant-table-guest-legacy',
    redirect: (to) => ({
      name: 'restaurant-table-guest',
      params: { token: to.params.token },
      query: to.query,
      hash: to.hash,
    }),
    meta: { hideLiveChat: true, publicLinkScope: 'table' },
  },
  {
    path: '/landing/:slug',
    name: 'landing-page',
    component: () => import('@/views/LandingPage.vue'),
  },
  {
    path: '/client-IDTag/:uuid',
    name: 'client-photo',
    component: () => import('@/views/client-image-upload/ClientPhotoUpload.vue'),
    props: true,
  },
  {
    path: '/passports/',
    name: 'passport-purchase',
    component: () => import('@/views/passports/PassportPurchaseView.vue'),
    meta: { hideLiveChat: true },
  },
  {
    path: '/passports/a/:code',
    name: 'passport-alliance-purchase',
    component: () => import('@/views/passports/PassportPurchaseView.vue'),
    meta: { hideLiveChat: true },
  },
  ...['dashboard', 'login', 'forgot', 'password'].map(mode => ({
    path: `/passports/alliances${mode === 'dashboard' ? '' : `/${mode}`}`,
    name: `passport-alliance-${mode}`,
    component: () => import('@/views/passports/PassportAlliancePortal.vue'),
    meta: { hideLiveChat: true, allianceMode: mode },
  })),
  {
    path: '/passports/gift-cards/:giftCardId',
    name: 'passport-gift-cards',
    component: () => import('@/views/passports/PassportGiftCardsView.vue'),
  },

  // ✅ TOOLS (lead magnets)
  {
    path: '/:magnetSlug/',
    name: 'tool-lead-magnet',
    component: () => import('@/views/tools/LeadMagnetRunner.vue'),
    props: true,
  },
  {
    path: '/:magnetSlug/resultado/',
    name: 'tool-lead-magnet-result',
    component: () => import('@/views/tools/LeadMagnetResult.vue'),
    props: true,
  },
  {
    path: '/:magnetSlug/final/',
    name: 'tool-lead-magnet-finish',
    component: () => import('@/views/tools/LeadMagnetFinish.vue'),
    props: true,
  },

  {
    path: '/landing-not-found',
    name: 'landing-not-found',
    component: () => import('@/views/LandingNotFound.vue'),
  },
  {
    path: '/company-inactive',
    name: 'company-inactive',
    component: () => import('@/views/CompanyInactive.vue'),
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/NotFound.vue'),
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});

router.beforeEach(async (to, from, next) => {
  let subdomain;
  const localCompanyId = process.env.VUE_APP_ENVIROMENT === 'dev'
    ? Number(to.query.company_id || 0)
    : 0;

  if (process.env.VUE_APP_ENVIROMENT === 'dev') {
    // subdomain = 'tools';
    subdomain = 'traveldesk';
  } else {
    const hostname = window.location.hostname;
    subdomain = hostname.split('.')[0];
  }

  const isToolsSubdomain = subdomain === 'tools';

  // ✅ Si estás en tools.traveldesk.mx, NO cargues companyData ni valides servicios.
  // Solo permitimos rutas de tools + not-found
  if (isToolsSubdomain) {
    const TOOLS_ROUTES = new Set([
      'tool-lead-magnet',
      'tool-lead-magnet-result',
      'tool-lead-magnet-finish',
      'not-found',
    ]);

    if (!TOOLS_ROUTES.has(to.name)) {
      // Evita que entren a / o /activities en tools
      return next({ name: 'not-found' });
    }

    return next();
  }

  // Rutas públicas que deben pasar sin bloqueos de servicios
  const PUBLIC_ROUTES = new Set([
    'not-found',
    'company-inactive',
    'landing-not-found',
    'client-photo',
    'passport-purchase',
    'passport-alliance-purchase',
    'passport-alliance-dashboard',
    'passport-alliance-login',
    'passport-alliance-forgot',
    'passport-alliance-password',
    'passport-gift-cards',
    'public-tour-reservation-confirmation',
    'public-restaurant-reservation-confirmation',
    'public-restaurant-benefits',
    'restaurant-menu-guest',
    'restaurant-table-guest',
  ]);

  if (PUBLIC_ROUTES.has(to.name)) {
    return next();
  }

  // Carga de company por subdominio (necesaria para header/colores/contacto)
  try {
    if (localCompanyId > 0) {
      await store.dispatch('fetchCompanyDataById', localCompanyId);
    } else {
      await store.dispatch('fetchCompanyData', subdomain);
    }
  } catch (e) {
    console.log('COMPANY DATA FETCH ERROR', e);
  }

  const companyData = store.getters.getCompanyData;
  if (!companyData) {
    console.log('COMPANY DATA NOT FOUND');
    return next({ name: 'not-found' });
  }

  // Rutas tipo landing que tienen su propia lógica de carga
  if (to.name === 'landing-page') {
    const slug = to.params.slug;
    const companyId = companyData.data.company_id;

    await store.dispatch('fetchLandingPageData', { slug, companyId });
    const landingData = store.getters.getLandingPageData;

    if (!landingData) {
      return next({ name: 'landing-not-found' });
    }
    return next();
  }

  // client-photo
  if (to.name === 'client-photo') {
    return next();
  }

  // Estado compañía
  const companyStatus = Number(companyData.data.company_status);
  const isVipCompany = companyData.data.is_VIP === true || Number(companyData.data.is_VIP) === 1;
  const hasExplicitBookingAccess = Object.prototype.hasOwnProperty.call(
    companyData.data,
    'booking_access_allowed'
  );
  const bookingAccessAllowed = companyData.data.booking_access_allowed === true
    || Number(companyData.data.booking_access_allowed) === 1;
  const bookingAccessReason = String(companyData.data.booking_access_reason || '');

  if (hasExplicitBookingAccess) {
    if (!bookingAccessAllowed) {
      if (bookingAccessReason === 'unsubscribed') {
        return next({ name: 'company-inactive' });
      }

      console.log('COMPANY ACCESS DENIED', bookingAccessReason);
      return next({ name: 'not-found' });
    }
  } else {
    // Compatibilidad con APIs anteriores durante despliegues escalonados.
    if (companyStatus === 4 && !isVipCompany) {
      return next({ name: 'company-inactive' });
    } else if (companyStatus > 1 && companyStatus < 4) {
      console.log('COMPANY DATA NOT FOUND ON STATUS');
      return next({ name: 'not-found' });
    }
  }

  // Servicios activos: puede venir como string JSON o array
  let activeServices = companyData.data.company_active_services ?? [];
  if (typeof activeServices === 'string') {
    try {
      activeServices = JSON.parse(activeServices);
    } catch {
      activeServices = [];
    }
  }
  if (!Array.isArray(activeServices)) activeServices = [];
  activeServices = activeServices.map(Number);

  const hasTransportation = activeServices.includes(1);
  const hasTours = activeServices.includes(2);
  const hasRestaurants = activeServices.includes(3);

  if (['booking-engine', 'tours-booking-engine', 'landing-page'].includes(to.name)) {
    await store.dispatch('TransportMarketingStore/initialize', to.query);
  }

  if (hasTransportation && hasTours) {
    if (to.name === 'booking-engine' || to.name === 'tours-booking-engine' || to.name === 'public-reservation-confirmation') return next();
    if (hasRestaurants && to.name === 'restaurant-booking-engine') return next();
    return next({ name: 'booking-engine' });
  }

  if (hasTransportation) {
    if (to.name === 'booking-engine' || to.name === 'public-reservation-confirmation') return next();
    if (hasRestaurants && to.name === 'restaurant-booking-engine') return next();
    return next({ name: 'not-found' });
  }

  if (hasTours) {
    if (to.name === 'booking-engine' || to.name === 'tours-booking-engine') return next();
    if (hasRestaurants && to.name === 'restaurant-booking-engine') return next();
    return next({ name: 'not-found' });
  }

  if (hasRestaurants) {
    if (to.name === 'booking-engine' || to.name === 'restaurant-booking-engine') return next();
    return next({ name: 'booking-engine' });
  }

  return next({ name: 'not-found' });
});

export default router;
